import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { ApiResponse } from '@shared/models/api-response';
import { map, of, switchMap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
export interface ControlPointInfoDto {
  ControlPointId: number;
  ControlPointName: string;
}

export interface GenerateReportRequestDto {
  recordId?: number;
  startDate?: string;
  endDate?: string;
  ponderationId?: number;
  standardCode?: string;
  eventName?: string;
  eventType?: string;
  location?: string;
  organisation?: string;
  controlPoints?: ControlPointInfoDto[];
  devices?: string[];
  language: string;
}

@Injectable({
  providedIn: 'root'
})
export class ReportService {
    private dataSubject = new BehaviorSubject<any>(null);
    private reportTriggerSource = new BehaviorSubject<boolean>(false);
    private pdfCompletionSource = new BehaviorSubject<boolean>(false);
    
    reportTrigger$ = this.reportTriggerSource.asObservable(); 
    pdfCompletion$ = this.pdfCompletionSource.asObservable();
    data$ = this.dataSubject.asObservable();
    
    private baseUrl = environment.vibrationApiURL;
    
    constructor(
      private httpClient: HttpClient,
      private translateService: TranslateService
    ) { }
  
    triggerPDFGeneration(value: boolean): Promise<void> {
      return new Promise((resolve, reject) => {
        if (value) {
          this.reportTriggerSource.next(value);
          const subscription = this.pdfCompletion$.subscribe(completed => {
            if (completed) {
              subscription.unsubscribe();
              resolve();
            }
          });
        } else {
          reject();
        }
      });
    }
    
    notifyPDFCompletion(): void {
      this.pdfCompletionSource.next(true);
      setTimeout(() => this.pdfCompletionSource.next(false), 100);
    }
    
    /**
     * Generate vibration report from backend
     * @param recordId - For single record report
     * @param startDate - Start date for multi-record report
     * @param endDate - End date for multi-record report
     * @param ponderationId - Optional ponderation ID
     * @param eventName - Optional event name
     * @param eventType - Optional event type
     * @param siteName - Site name (location)
     * @param organisation - Optional organisation
     * @param controlPoints - List of control points for multi-record report
     * @returns Observable<Blob> - PDF file as blob
     */
    generateVibrationReport(
      recordId?: number,
      startDate?: Date,
      endDate?: Date,
      ponderationId?: number,
      eventName?: string,
      eventType?: string,
      siteName?: string,
      organisation?: string,
      controlPoints?: ControlPointInfoDto[],
      siteId?: number,
      language?: string,
      devices: string[] | null = null
    ): Observable<Blob> {
      if (!siteId) {
        return this.sendReportRequest(recordId, startDate, endDate, ponderationId, eventName, eventType, siteName, organisation, controlPoints, undefined, language, devices);
      }

      return this.getSiteStandardCode(siteId).pipe(
        switchMap((standardCode) =>
          this.sendReportRequest(recordId, startDate, endDate, ponderationId, eventName, eventType, siteName, organisation, controlPoints, standardCode, language, devices)
        )
      );
    }

    private sendReportRequest(
      recordId?: number,
      startDate?: Date,
      endDate?: Date,
      ponderationId?: number,
      eventName?: string,
      eventType?: string,
      siteName?: string,
      organisation?: string,
      controlPoints?: ControlPointInfoDto[],
      standardCode?: string,
      language?: string,
      devices: string[] | null = null

    ): Observable<Blob> {
      const request: GenerateReportRequestDto = {
        ...(recordId ? { recordId } : {}),
        ...(startDate ? { startDate: this.toLocalDateString(startDate) } : {}),
        ...(endDate ? { endDate: this.toLocalDateString(endDate) } : {}),
        ...(ponderationId ? { ponderationId } : {}),
        ...(standardCode ? { standardCode } : {}),
        ...(eventName ? { eventName } : {}),
        ...(eventType ? { eventType } : {}),
        ...(siteName ? { location: siteName } : {}),
        ...(organisation ? { organisation } : {}),
        ...(controlPoints?.length ? { controlPoints } : {}),
        ...(devices?.length ? { devices } : {}),

        language: this.resolveReportLanguage(language)
      };

      return this.httpClient.post(`${this.baseUrl}/report/vibration`, request, {
        responseType: 'blob'
      });
    }

    private getSiteStandardCode(siteId: number): Observable<string | undefined> {
      return this.httpClient.get<ApiResponse<string | null>>(`${this.baseUrl}/ponderation/site/${siteId}/standard-code`).pipe(
        map((response) => response?.data ?? undefined)
      );
    }

    private resolveReportLanguage(language?: string): string {
      const selectedLanguage = (
        language ||
        this.translateService.currentLang ||
        this.translateService.getDefaultLang() ||
        'en'
      ).toLowerCase();

      return ['en', 'fr', 'es'].includes(selectedLanguage) ? selectedLanguage : 'en';
    }
    
    
    private toLocalDateString(date: Date, endOfDay = false): string {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const time = endOfDay ? '23:59:59' : '00:00:00';
      return `${y}-${m}-${d}T${time}`;
    }
}
